import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, Loader2, AlertTriangle } from "lucide-react";
import { fmtDate } from "@/components/legal/AgreementBody";

const LINK_LABEL = {
  MERCHANT_AGREEMENT: "View full Merchant Terms & Conditions",
  SHOPPER_TERMS: "View full Shopper Terms & Conditions",
  PRIVACY_NOTICE: "View Privacy Notice",
};

function ReviewSection({ section }) {
  const blocks = [];
  let list = [];
  const flush = () => { if (list.length) blocks.push(<ul key={`u${blocks.length}`} className="list-disc pl-6 space-y-0.5">{list}</ul>); list = []; };
  section.lines.forEach((l, i) => {
    if (l.startsWith("- ")) { list.push(<li key={i}>{l.slice(2)}</li>); return; }
    flush();
    blocks.push(<p key={i}>{l}</p>);
  });
  flush();
  return (
    <section aria-labelledby={`terms-sec-${section.number}`} data-testid={`terms-review-section-${section.number}`}>
      <h3 id={`terms-sec-${section.number}`} className="font-semibold text-slate-900">{section.number}. {section.title}</h3>
      <div className="text-sm text-slate-700 space-y-1.5 mt-1 leading-relaxed">{blocks}</div>
    </section>
  );
}

function DocLinks({ docs }) {
  return (
    <ul className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="terms-doc-links">
      {docs.map((d) => (
        <li key={d.agreement_id} className="px-5 py-3 flex flex-wrap items-center justify-between gap-2">
          <a href={`/terms/documents/${d.agreement_id}`} target="_blank" rel="noreferrer"
             className="text-sm font-semibold text-cyan-800 underline inline-flex items-center gap-1" data-testid={`terms-doc-link-${d.agreement_type}`}>
            {LINK_LABEL[d.agreement_type] || `View full ${d.title}`} <ExternalLink className="w-3 h-3" aria-hidden="true" />
          </a>
          <span className="text-xs text-slate-500" data-testid={`terms-doc-version-${d.agreement_type}`}>
            Version: {d.version} · Effective: {fmtDate(d.effective_at)}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function LegalAcceptForm({ actor, missing, current = [], onAccepted, submitLabel }) {
  const isReaccept = missing.some((m) => m.state === "reacceptance_required");
  const [copy, setCopy] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);
  useEffect(() => {
    window.scrollTo?.(0, 0);
    api.get("/legal/review", { params: { actor } }).then((r) => setCopy(r.data)).catch(() => toast.error("Could not load the Terms review."));
  }, [actor]);

  const missingIds = new Set(missing.map((m) => m.agreement_id));
  const docs = [...missing, ...current.filter((c) => !missingIds.has(c.agreement_id) && c.state !== "not_required")];
  const changed = missing.filter((m) => m.state === "reacceptance_required" && m.change_summary);

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed || busy) return;
    try {
      setBusy(true);
      await api.post("/legal/accept", {
        agreement_ids: missing.map((m) => m.agreement_id),
        acceptance_ui_mode: "SINGLE_CHECKBOX",
        single_checkbox_confirmed: true,
        marketing_consent: isReaccept ? null : marketing,
      });
      await onAccepted();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not record your terms acceptance. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    // On small screens the portal nav renders above the gate; bring the Terms screen into view.
    if (copy && window.innerWidth < 1024) formRef.current?.scrollIntoView?.({ block: "start" });
  }, [copy]);

  if (!copy) return <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <form ref={formRef} onSubmit={submit} className="max-w-3xl mx-auto py-8 space-y-6 px-1 scroll-mt-20" data-testid="legal-gate">
      <header>
        {isReaccept ? (
          <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-amber-700" data-testid="legal-reaccept-badge">
            <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> Important Terms Update
          </div>
        ) : (
          <div className="text-xs font-bold uppercase tracking-widest text-cyan-700">Terms acceptance</div>
        )}
        <h1 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2" data-testid="legal-gate-title">{copy.title}</h1>
        <p className="text-sm text-slate-600 mt-2" data-testid="legal-gate-subtitle">{copy.subtitle}</p>
      </header>

      {changed.map((m) => (
        <div key={m.agreement_id} className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900" data-testid={`legal-change-summary-${m.agreement_type}`}>
          <div className="font-semibold mb-0.5">What changed</div>
          {m.change_summary}
          <div className="text-xs text-amber-800 mt-1">This summary is informational. The full Terms & Conditions remain authoritative.</div>
        </div>
      ))}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-5" data-testid="terms-review">
        {copy.sections.map((s) => <ReviewSection key={s.number} section={s} />)}
        <p className="text-xs text-slate-500 border-t border-slate-100 pt-4">
          This is a summary of important points. The full Terms & Conditions and Privacy Notice below are the authoritative documents you are accepting.
        </p>
      </div>

      <DocLinks docs={docs} />

      <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <legend className="sr-only">Terms acceptance</legend>
        <label htmlFor="terms-accept" className="flex items-start gap-3 cursor-pointer text-sm text-slate-900 leading-snug font-medium">
          <input id="terms-accept" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required
                 className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-400 accent-cyan-700" data-testid="legal-check-single" />
          <span data-testid="legal-check-single-label">{copy.acceptance_statement}</span>
        </label>
      </fieldset>

      {!isReaccept && (
        <fieldset className="rounded-2xl border border-dashed border-slate-300 p-5" data-testid="legal-marketing-section">
          <legend className="px-1 text-xs font-semibold uppercase tracking-widest text-slate-500">Optional</legend>
          <label htmlFor="terms-marketing" className="flex items-start gap-3 cursor-pointer text-sm text-slate-600 leading-snug">
            <input id="terms-marketing" type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)}
                   className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-400 accent-cyan-700" data-testid="legal-check-marketing" />
            <span>{copy.marketing_statement}</span>
          </label>
        </fieldset>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <button type="submit" disabled={!agreed || busy} data-testid="legal-accept-submit"
                className="pf-btn-primary px-7 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {submitLabel || "Agree & Continue"}
        </button>
        {!agreed && <span className="text-xs text-slate-500" data-testid="legal-accept-hint">Tick the box above to continue.</span>}
      </div>
    </form>
  );
}
